import React, { useEffect, useState, useRef } from 'react';

import { makeStyles, MenuItem } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { EmptyBar } from 'screens/analytics/commonComponents';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { Divider1, StyledBox, StyledTooltip, StyledTypography, NewStyledIconContainer } from 'styles/common';

import { StarIcon, CheckIcon, CloseIcon } from 'constants/icons';
import theme from 'constants/theme';

import { getFilters as getFiltersReview } from 'services/analytics/review';

import CohortCreation9BoxGrid from './CohortCreation9BoxGrid';
import { onClickPreCheck } from './CohortCreationFeedback';
import { ExpandedView } from './CohortSidePanel';
import { useCohortCreationContext } from './CohortSidePanelContext';
import { DropdownContainer, RangeSelector, SelectListComp, StyledSelect } from './CommonComponents';

const PROMOTION_ENUMS = {
  PROMOTE: 'PROMOTE',
  NO_CHANGE: 'NO_CHANGE',
  PIP: 'PIP',
};

const PromotionCompMap = {
  [PROMOTION_ENUMS.PROMOTE]: {
    id: PROMOTION_ENUMS.PROMOTE,
    text: 'Promoted',
    color: 'green',
    getComponent: (calibration, size = 'header') => (
      <NewStyledIconContainer color={calibration ? 'green' : theme.palette.text.secondaryLight} size={size}>
        <CheckIcon size={size} />
      </NewStyledIconContainer>
    ),
  },
  [PROMOTION_ENUMS.NO_CHANGE]: {
    id: PROMOTION_ENUMS.NO_CHANGE,
    text: 'No Change',
    color: 'textSecondary',
    getComponent: (calibration, height = null, width = null) => (
      <EmptyBar height={height} width={width} selected={Boolean(calibration)} />
    ),
  },
  [PROMOTION_ENUMS.PIP]: {
    id: PROMOTION_ENUMS.PIP,
    text: 'PIP',
    color: 'red',
    getComponent: (calibration, size = 'header') => (
      <NewStyledIconContainer color={calibration ? 'red' : theme.palette.text.secondaryLight} size={size}>
        <CloseIcon size={size} />
      </NewStyledIconContainer>
    ),
  },
};

const MODULE_NAME = 'review';

const useStyles = makeStyles({
  paper: {
    boxShadow: '3px 4px 5px 0px #0000001C',
    border: `1px solid ${theme.palette.borders.lighterGrey}`,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
});

const ReviewSource = ({ onClick, accessibleRole, isAllEmployeesSelected, moduleName }) => {
  const [loading, setLoading] = useState(true);
  const [allSources, setAllSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const fetchFilters = () => {
    setLoading(true);
    getFiltersReview()
      .then((res) => {
        setAllSources(res.sources);
        setSelectedSource(res.sources[0]);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  const dispatch = useDispatch();

  const { updateBaseCohort } = useCohortCreationContext();
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  useEffect(() => {
    if (selectedSource) {
      onClick(selectedSource);
    }
  }, [selectedSource]);

  const classes = useStyles();

  const setSelectedSourceCurried = (target) => () => {
    setSelectedSource(target.value);
  };

  if (loading) {
    return <Skeleton width="70%" height="2rem" />;
  }

  return (
    <>
      <StyledBox height="2.5rem" display="flex" alignItems="center" justifyContent="space-between">
        <StyledTypography variant="body2" color="textSecondary" rightGutter={2}>
          Source
        </StyledTypography>
        <StyledSelect
          style={{ height: '100%', width: '10rem' }}
          variant="outlined"
          displayEmpty
          value={selectedSource}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
            PopoverClasses: {
              paper: classes.paper,
            },
          }}
          onChange={({ target }) => {
            onClickPreCheck(
              dispatch,
              updateBaseCohort,
              setSelectedSourceCurried(target),
              accessibleRole,
              isAllEmployeesSelected,
              moduleName,
              enabledFeatures
            );
          }}
        >
          {allSources.map((source, index) => (
            <MenuItem key={`source-option-${index}`} value={source}>
              <StyledTooltip title={source.displayName}>
                <StyledTypography
                  style={{ whiteSpace: 'no-wrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                  variant="body2"
                >
                  {source.displayName}
                </StyledTypography>
              </StyledTooltip>
            </MenuItem>
          ))}
        </StyledSelect>
      </StyledBox>
      <Divider1 topGutter={3} />
    </>
  );
};

const ReviewPromotion = ({ sectionId, accessibleRole, isAllEmployeesSelected }) => {
  const PROMOTION_ENUMS = {
    PROMOTE: {
      key: 'PROMOTE',
      label: 'Promote',
    },
    NO_CHANGE: {
      key: 'NO_CHANGE',
      label: 'No Change',
    },
    PIP: {
      key: 'PIP',
      label: 'PIP',
    },
  };
  const getPromotionRows = (promotionKey) => () => {
    if (promotionKey === PROMOTION_ENUMS.NO_CHANGE.key) {
      return <>{PromotionCompMap[promotionKey].getComponent(true, '0.25rem', '0.725rem')}</>;
    }
    return <>{PromotionCompMap[promotionKey].getComponent(true, 'tinierFixed')}</>;
  };

  return (
    <>
      <StyledTypography variant="body2" weight={600}>
        Promotion Status
      </StyledTypography>
      <SelectListComp
        options={Object.values(PROMOTION_ENUMS)}
        sectionId={sectionId}
        preLabelComp={getPromotionRows}
        isMultiSelect
        dataKey="promotionStatus"
        isAllEmployeesSelected={isAllEmployeesSelected}
        accessibleRole={accessibleRole}
        moduleName={MODULE_NAME}
      />
    </>
  );
};

/**
 * @deprecated 09-01-2025 confirm before deletion: https://peoplemesh.slack.com/archives/C01AYG5JEAF/p1736420210454079
 */
function CohortCreationReview({
  content,
  sectionLabel,
  sectionId,
  onHeaderClick,
  accessibleRole,
  isAllEmployeesSelected,
}) {
  const dispatch = useDispatch();
  const { updateBaseCohort } = useCohortCreationContext();

  const latestValue = useRef(isAllEmployeesSelected);

  useEffect(() => {
    latestValue.current = isAllEmployeesSelected;
  }, [isAllEmployeesSelected]);
  return (
    <>
      <ExpandedView
        content={content}
        sectionLabel={sectionLabel}
        sectionId={sectionId}
        headerComponent={() => (
          <ReviewSource
            onClick={(selectedValue) => onHeaderClick(sectionId, 'source', selectedValue)}
            isAllEmployeesSelected={isAllEmployeesSelected}
            accessibleRole={accessibleRole}
            moduleName={MODULE_NAME}
          />
        )}
      />
      <DropdownContainer topGutter={3}>
        <RangeSelector
          sectionId={sectionId}
          minValue={0}
          maxValue={10}
          inputAdornmentComp={() => <StarIcon size="tinierFixed" color="mustard" />}
          dataKey="data"
          headingText="Score (Range)"
          thumbsAreInCenter
          isAllEmployeesSelected={isAllEmployeesSelected}
          accessibleRole={accessibleRole}
          moduleName={MODULE_NAME}
        />
      </DropdownContainer>
      <DropdownContainer topGutter={3}>
        <ReviewPromotion
          sectionId={sectionId}
          isAllEmployeesSelected={isAllEmployeesSelected}
          accessibleRole={accessibleRole}
          moduleName={MODULE_NAME}
        />
      </DropdownContainer>
      <DropdownContainer>
        <CohortCreation9BoxGrid
          sectionId={sectionId}
          dataKey="gridPositions"
          isAllEmployeesSelected={isAllEmployeesSelected}
          accessibleRole={accessibleRole}
          moduleName={MODULE_NAME}
        />
      </DropdownContainer>
    </>
  );
}

export default CohortCreationReview;
